import { hasExactly, intWithin, isPlainRecord, marketRuleSetting, resolveCountryRule, resolveMarketRule } from './market-rule-kit.js';
// ---- driver.signup ---------------------------------------------------------------------------------------------------------
export const DRIVER_SIGNUP_KEY = 'driver.signup';
/** The personas a sign-up can offer, in display order: `driver` (passenger rides), `courier` (deliveries). */
export const DRIVER_SIGNUP_PERSONAS = ['driver', 'courier'];
/** How the invitation code is entered: behind a collapsed link (the recommendation), always visible, or not at all. */
export const DRIVER_SIGNUP_INVITE_CODE_ENTRIES = ['HIDDEN_LINK', 'VISIBLE', 'OFF'];
export const DRIVER_SIGNUP_BOUNDS = { draftRetentionDays: { min: 1, max: 90 } };
/** What an ungoverned market reads: sign-up open for both personas, an unfinished sign-up kept 30 days, the code behind a link. */
export const DEFAULT_DRIVER_SIGNUP = { enabled: true, personas: ['driver', 'courier'], draftRetentionDays: 30, inviteCodeEntry: 'HIDDEN_LINK' };
/** What an unreadable version reads: sign-up closed (nothing is saved or created on a rule nobody can read). */
export const STRICT_DRIVER_SIGNUP = { enabled: false, personas: ['driver'], draftRetentionDays: DRIVER_SIGNUP_BOUNDS.draftRetentionDays.min, inviteCodeEntry: 'OFF' };
export const DRIVER_SIGNUP_POLICY_REASON_CODES = ['DRIVER_SIGNUP_POLICY_GOVERNED', 'DRIVER_SIGNUP_POLICY_DEFAULT', 'DRIVER_SIGNUP_POLICY_INVALID'];
const orderedPersonas = (ps) => DRIVER_SIGNUP_PERSONAS.filter((p) => ps.includes(p));
/** The stable issue of a value that is not a `driver.signup` value, or undefined when it is one. */
export function driverSignupIssue(v) {
    if (!isPlainRecord(v))
        return 'NOT_AN_OBJECT';
    if (!hasExactly(v, ['enabled', 'personas', 'draftRetentionDays', 'inviteCodeEntry']))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.enabled !== 'boolean')
        return 'INVALID_ENABLED';
    const ps = v.personas;
    if (!Array.isArray(ps) || ps.length < 1 || ps.some((p) => !DRIVER_SIGNUP_PERSONAS.includes(p)) || new Set(ps).size !== ps.length)
        return 'INVALID_PERSONAS';
    const b = DRIVER_SIGNUP_BOUNDS;
    if (!intWithin(v.draftRetentionDays, b.draftRetentionDays.min, b.draftRetentionDays.max))
        return 'INVALID_DRAFT_RETENTION_DAYS';
    if (!DRIVER_SIGNUP_INVITE_CODE_ENTRIES.includes(v.inviteCodeEntry))
        return 'INVALID_INVITE_CODE_ENTRY';
    return undefined;
}
export const DRIVER_SIGNUP_SPEC = {
    key: DRIVER_SIGNUP_KEY,
    issue: driverSignupIssue,
    copy: (v) => ({ enabled: v.enabled, personas: orderedPersonas(v.personas), draftRetentionDays: v.draftRetentionDays, inviteCodeEntry: v.inviteCodeEntry }),
    defaultValue: DEFAULT_DRIVER_SIGNUP,
    strictValue: STRICT_DRIVER_SIGNUP,
    reasonPrefix: 'DRIVER_SIGNUP_POLICY',
};
export const driverSignupSetting = (input) => marketRuleSetting(DRIVER_SIGNUP_SPEC, input);
export const resolveDriverSignup = (settings, marketId, at, regionCode) => resolveMarketRule(DRIVER_SIGNUP_SPEC, settings, marketId, at, regionCode);
/** What a country decides while no market is chosen yet (an unfinished sign-up still on its first step). */
export const resolveDriverSignupForCountry = (settings, countryCode, at) => resolveCountryRule(DRIVER_SIGNUP_SPEC, settings, countryCode, at);
/** The wire form of a decision (what the provider app applies itself). */
export function driverSignupWire(d) {
    return { enabled: d.value.enabled, personas: d.value.personas, draftRetentionDays: d.value.draftRetentionDays, inviteCodeEntry: d.value.inviteCodeEntry, reasonCode: d.reasonCode, policyVersion: d.policyVersion };
}
// ---- driver.signup.minimumAgeYears -------------------------------------------------------------------------------------------
export const DRIVER_MINIMUM_AGE_KEY = 'driver.signup.minimumAgeYears';
export const DRIVER_MINIMUM_AGE_BOUNDS = { years: { min: 16, max: 30 } };
/** The recommendation (99 and Uber take drivers and couriers from 18); a country pack's higher floor still applies. */
export const DEFAULT_DRIVER_MINIMUM_AGE = { driver: 18, courier: 18 };
/** An unreadable version: the highest age the key allows - never a looser reading of a legal minimum. */
export const STRICT_DRIVER_MINIMUM_AGE = { driver: DRIVER_MINIMUM_AGE_BOUNDS.years.max, courier: DRIVER_MINIMUM_AGE_BOUNDS.years.max };
export const DRIVER_MINIMUM_AGE_REASON_CODES = ['DRIVER_MINIMUM_AGE_GOVERNED', 'DRIVER_MINIMUM_AGE_DEFAULT', 'DRIVER_MINIMUM_AGE_INVALID'];
/** The stable refusal of a person younger than the minimum age in force (the sign-up and the licence review share it). */
export const BELOW_MINIMUM_AGE = 'BELOW_MINIMUM_AGE';
export function driverMinimumAgeIssue(v) {
    if (!isPlainRecord(v))
        return 'NOT_AN_OBJECT';
    if (!hasExactly(v, DRIVER_SIGNUP_PERSONAS))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    const b = DRIVER_MINIMUM_AGE_BOUNDS.years;
    for (const p of DRIVER_SIGNUP_PERSONAS)
        if (!intWithin(v[p], b.min, b.max))
            return 'INVALID_YEARS';
    return undefined;
}
export const DRIVER_MINIMUM_AGE_SPEC = {
    key: DRIVER_MINIMUM_AGE_KEY,
    issue: driverMinimumAgeIssue,
    copy: (v) => ({ driver: v.driver, courier: v.courier }),
    defaultValue: DEFAULT_DRIVER_MINIMUM_AGE,
    strictValue: STRICT_DRIVER_MINIMUM_AGE,
    reasonPrefix: 'DRIVER_MINIMUM_AGE',
    constraintClass: 'legal',
    // A state or a market may only raise the age of the layers above it.
    tighten: (d, e) => ({ driver: Math.max(d.driver, e.driver), courier: Math.max(d.courier, e.courier) }),
};
export const driverMinimumAgeSetting = (input) => marketRuleSetting(DRIVER_MINIMUM_AGE_SPEC, input);
export const resolveDriverMinimumAge = (settings, marketId, at, regionCode) => resolveMarketRule(DRIVER_MINIMUM_AGE_SPEC, settings, marketId, at, regionCode);
/** The age that decides for `persona`: the governed value, never below the country pack's own floor (its licence's minimum age; absent = no floor). */
export function effectiveMinimumAgeYears(decision, persona, countryFloorYears) {
    const governed = decision.value[persona];
    return countryFloorYears !== undefined && Number.isSafeInteger(countryFloorYears) && countryFloorYears > governed ? countryFloorYears : governed;
}
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isIsoDate = (v) => ISO_DATE.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) && new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v;
/** Whole years between two ISO dates (`YYYY-MM-DD`). */
function wholeYears(from, to) {
    const [fy, fm, fd] = from.split('-').map(Number);
    const [ty, tm, td] = to.split('-').map(Number);
    return ty - fy - (tm < fm || (tm === fm && td < fd) ? 1 : 0);
}
/**
 * Whether a person born on `birthDate` may sign up as `persona` on `today` (the operating market's local date). Runs when the birth date becomes known (the licence
 * review); the answer names the age that decided and the version that set it.
 */
export function checkDriverMinimumAge(input) {
    const minimumAgeYears = effectiveMinimumAgeYears(input.decision, input.persona, input.countryFloorYears);
    const policyVersion = input.decision.policyVersion;
    if (!isIsoDate(input.birthDate) || !isIsoDate(input.today) || input.birthDate > input.today)
        return { ok: false, reasonCode: 'BIRTH_DATE_INVALID', minimumAgeYears, policyVersion };
    if (wholeYears(input.birthDate, input.today) < minimumAgeYears)
        return { ok: false, reasonCode: BELOW_MINIMUM_AGE, minimumAgeYears, policyVersion };
    return { ok: true, minimumAgeYears, policyVersion };
}
//# sourceMappingURL=driver-signup-policy.js.map